import type {
  AddManualChatMessageInput,
  EvergreenChatAuthorRole,
  EvergreenManualAttendeeLabel,
  EvergreenTimelineEvent,
  ParticipationStatusCriteria,
  ParticipationStatusDto,
} from '@/features/webinar/types';

export const MANUAL_ATTENDEE_LABELS: readonly EvergreenManualAttendeeLabel[] = [
  'TOP_25_PERCENT',
  'TOP_50_PERCENT',
  'TOP_75_PERCENT',
  'TOP_100_PERCENT',
  'CONVERSION',
];

const AUTHOR_ROLES: readonly EvergreenChatAuthorRole[] = [
  'PRESENTER',
  'MODERATOR',
  'SPEAKER',
  'ATTENDEE',
];

export function isManualAttendeeLabel(value: unknown): value is EvergreenManualAttendeeLabel {
  return MANUAL_ATTENDEE_LABELS.some((label) => label === value);
}

function isAuthorRole(value: unknown): value is EvergreenChatAuthorRole {
  return AUTHOR_ROLES.some((role) => role === value);
}

export interface ChatMessageMetadata {
  author: string;
  role: EvergreenChatAuthorRole;
  statusCriteria: EvergreenManualAttendeeLabel | null;
}

function parseMetadata(metadata: string | null): Record<string, unknown> | null {
  if (!metadata) return null;
  try {
    const parsed: unknown = JSON.parse(metadata);
    return parsed !== null && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function readChatMessageMetadata(metadata: string | null): ChatMessageMetadata {
  const fallback: ChatMessageMetadata = { author: '', role: 'ATTENDEE', statusCriteria: null };
  const parsed = parseMetadata(metadata);
  if (!parsed) return fallback;
  const { author, role, statusCriteria } = parsed;
  return {
    author: typeof author === 'string' ? author : fallback.author,
    role: isAuthorRole(role) ? role : fallback.role,
    statusCriteria: isManualAttendeeLabel(statusCriteria) ? statusCriteria : null,
  };
}

const ALL_STATUS_CRITERIA: Record<ParticipationStatusCriteria, true> = {
  PRESENTER: true,
  MODERATOR: true,
  FIRST_SCORE: true,
  SECOND_SCORE: true,
  THIRD_SCORE: true,
  TOP_25_PERCENT: true,
  TOP_50_PERCENT: true,
  TOP_75_PERCENT: true,
  TOP_100_PERCENT: true,
  CONVERSION: true,
};

function isStatusCriteria(value: unknown): value is ParticipationStatusCriteria {
  return typeof value === 'string' && Object.hasOwn(ALL_STATUS_CRITERIA, value);
}

function nonBlankString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export interface ChatMessageLabel {
  label: string;
  color: string | null;
}

/**
 * Même règle que la diffusion Evergreen : un critère connu prend le label configuré sur le
 * webinaire (aucun s'il a été supprimé), sinon on garde le libellé figé dans le message.
 */
export function resolveChatMessageLabel(
  metadata: string | null,
  statuses: readonly Pick<ParticipationStatusDto, 'criteria' | 'label' | 'color'>[],
): ChatMessageLabel | null {
  const parsed = parseMetadata(metadata);
  if (!parsed) return null;
  if (isStatusCriteria(parsed.statusCriteria)) {
    const status = statuses.find((s) => s.criteria === parsed.statusCriteria);
    const label = nonBlankString(status?.label);
    return label ? { label, color: nonBlankString(status?.color) } : null;
  }
  const label = nonBlankString(parsed.statusLabel);
  return label ? { label, color: nonBlankString(parsed.statusColor) } : null;
}

/**
 * Un message manuel stocke son texte brut dans payloadJson, d'autres sources un JSON (chaîne ou
 * objet { content }). Un texte brut qui se trouve être du JSON valide (« 42 », « true ») reste
 * le texte saisi.
 */
export function readChatMessageContent(
  event: Pick<EvergreenTimelineEvent, 'payloadJson' | 'title'>,
): string {
  if (!event.payloadJson) return event.title ?? '';
  try {
    const parsed: unknown = JSON.parse(event.payloadJson);
    if (typeof parsed === 'string') return parsed;
    if (parsed !== null && typeof parsed === 'object') {
      const { content } = parsed as Record<string, unknown>;
      if (typeof content === 'string') return content;
    }
    return event.payloadJson;
  } catch {
    return event.payloadJson;
  }
}

export function isEditableManualMessage(
  event: Pick<EvergreenTimelineEvent, 'eventType' | 'origin'>,
): boolean {
  return event.eventType === 'SUPPORT_MESSAGE' && event.origin === 'MANUAL';
}

export interface ManualChatMessageFormValues {
  hours: number;
  minutes: number;
  seconds: number;
  authorName: string;
  authorRole: EvergreenChatAuthorRole;
  content: string;
  statusCriteria: EvergreenManualAttendeeLabel | null;
}

export function manualMessageToFormValues(
  event: EvergreenTimelineEvent,
): ManualChatMessageFormValues {
  const { author, role, statusCriteria } = readChatMessageMetadata(event.metadata);
  const offset = Math.max(0, Math.floor(event.startOffsetSeconds));
  return {
    hours: Math.floor(offset / 3600),
    minutes: Math.floor((offset % 3600) / 60),
    seconds: offset % 60,
    authorName: author,
    authorRole: role,
    content: readChatMessageContent(event),
    statusCriteria: role === 'ATTENDEE' ? statusCriteria : null,
  };
}

export function buildManualChatMessageInput(
  values: ManualChatMessageFormValues,
): AddManualChatMessageInput {
  return {
    hours: values.hours,
    minutes: values.minutes,
    seconds: values.seconds,
    authorName: values.authorName.trim(),
    authorRole: values.authorRole,
    content: values.content.trim(),
    statusCriteria: values.authorRole === 'ATTENDEE' ? values.statusCriteria : null,
  };
}

export function formatAttendeeLabelOption(criteriaName: string, configuredLabel?: string): string {
  const customLabel = configuredLabel?.trim();
  if (!customLabel || customLabel === criteriaName) return criteriaName;
  return `${criteriaName} (${customLabel})`;
}

// Limites des colonnes chat_messages côté back.
export const CHAT_AUTHOR_NAME_MAX_LENGTH = 160;
export const CHAT_CONTENT_MAX_LENGTH = 2000;

const CHAT_ERROR_TRANSLATION_KEYS: Record<string, string> = {
  'HTTP.ERROR.400.EVERGREEN.CHAT.AUTHOR_NAME.TOO_LONG':
    'chatEvergreenForm.errors.authorNameTooLong',
  'HTTP.ERROR.400.EVERGREEN.CHAT.CONTENT.TOO_LONG': 'chatEvergreenForm.errors.contentTooLong',
  'HTTP.ERROR.400.EVERGREEN.CHAT_CSV.AUTHOR.TOO_LONG': 'chatEvergreenForm.errors.csvAuthorTooLong',
  'HTTP.ERROR.400.EVERGREEN.CHAT_CSV.MESSAGE.TOO_LONG':
    'chatEvergreenForm.errors.csvMessageTooLong',
  'HTTP.ERROR.400.EVERGREEN.CHAT_CSV.QUOTES.INVALID': 'chatEvergreenForm.errors.csvQuotesInvalid',
  'HTTP.ERROR.400.EVERGREEN.CHAT_CSV.OFFSET.INVALID': 'chatEvergreenForm.errors.csvOffsetInvalid',
};

function detailMessages(details: unknown): string[] {
  if (!Array.isArray(details)) return [];
  return details
    .map((detail: unknown) =>
      detail && typeof detail === 'object' && 'message' in detail ? detail.message : null,
    )
    .filter((message): message is string => typeof message === 'string');
}

/** Les erreurs de validation du back arrivent sous un code générique, le code précis est dans details. */
export function resolveChatErrorTranslationKey(
  error: { message?: string; apiCode?: string; details?: unknown } | undefined,
): string | null {
  const codes = [error?.apiCode, error?.message, ...detailMessages(error?.details)];
  for (const code of codes) {
    if (code && Object.hasOwn(CHAT_ERROR_TRANSLATION_KEYS, code)) {
      return CHAT_ERROR_TRANSLATION_KEYS[code];
    }
  }
  return null;
}
